import { createElement, forwardRef, type ComponentType, type CSSProperties } from 'react'

type TransientProps = { [key: `$${string}`]: unknown }

/**
 * Linaria's `styled(Component)` forwards every prop to the wrapped component, and
 * framer-motion passes props it does not own to the DOM. styled-components
 * stripped `$`-prefixed (transient) props before either saw them; this restores
 * that for a wrapped component, so `$size` / `$active` style the element without
 * becoming attributes.
 *
 * Typed with a plain `style` so Linaria accepts prop interpolations (it sets
 * per-element CSS variables through `style`); motion components merge it.
 */
export function omitTransientProps<P extends { style?: unknown }>(
  Component: ComponentType<P>,
): ComponentType<Omit<P, 'style'> & { style?: CSSProperties } & TransientProps> {
  const Filtered = forwardRef<unknown, Record<string, unknown>>((props, ref) => {
    const own: Record<string, unknown> = {}
    for (const key of Object.keys(props)) if (!key.startsWith('$')) own[key] = props[key]
    return createElement(Component as ComponentType<Record<string, unknown>>, { ...own, ref })
  })
  Filtered.displayName = `omitTransientProps(${(Component as { displayName?: string }).displayName ?? 'Component'})`
  return Filtered as unknown as ComponentType<Omit<P, 'style'> & { style?: CSSProperties } & TransientProps>
}
